import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    App,
    Button,
    Card,
    Descriptions,
    Empty,
    List,
    Skeleton,
    Space,
    Table,
    Tag,
    Typography,
} from "antd";
import { ArrowLeftOutlined, NodeIndexOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/zh-cn";

import { getWorkItemDetail } from "../../../api/v3/mailHubApi";
import {
    formatMailHubError,
    readMailHubData,
    validateWorkItemDetailPayload,
} from "../lib/contracts";

dayjs.extend(relativeTime);
dayjs.locale("zh-cn");

const { Title, Text } = Typography;

const WORK_ITEM_FIELD_ORDER = [
    "id",
    "target_type",
    "target_id",
    "mailbox_receipt_id",
    "classification_id",
    "item_type",
    "title",
    "description",
    "processing_mode",
    "execution_kind",
    "pool_profile",
    "status",
    "priority",
    "assigned_to",
    "assigned_at",
    "locked_by",
    "locked_at",
    "version",
    "result_summary",
    "completed_at",
    "max_retries",
    "retry_count",
    "agent_task_id",
    "agent_dispatch_attempt_id",
    "execution_attempt_id",
    "dispatched_at",
    "trigger_source_id",
    "transition_id",
    "fan_out_group_id",
    "fan_out_index",
    "idempotency_key",
    "created_at",
    "updated_at",
];

function statusColor(status) {
    const map = {
        pending: "warning",
        in_progress: "processing",
        dispatched: "blue",
        completed: "success",
        failed: "error",
        dead: "red",
        cancelled: "default",
    };
    return map[status];
}

function formatValue(value) {
    if (value === null || value === undefined || value === "") return <Text type="secondary">—</Text>;
    if (typeof value === "boolean") return value ? "true" : "false";
    if (typeof value === "number") return String(value);
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value) && dayjs(value).isValid()) {
        return `${dayjs(value).format("YYYY-MM-DD HH:mm:ss")} (${dayjs(value).fromNow()})`;
    }
    return String(value);
}

function JsonBlock({ value }) {
    if (value === null || value === undefined) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="无数据" />;
    }
    return (
        <pre style={{ maxHeight: 420, overflow: "auto", padding: 12, background: "#111827", color: "#d1d5db", borderRadius: 6, fontSize: 12 }}>
            {JSON.stringify(value, null, 2)}
        </pre>
    );
}

const WorkItemDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { message } = App.useApp();
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [loadError, setLoadError] = useState(null);

    const loadData = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const response = await getWorkItemDetail(id);
            setDetail(validateWorkItemDetailPayload(readMailHubData(response, "Work Item 详情")));
            setLoadError(null);
        } catch (error) {
            const errorMessage = formatMailHubError(error, "加载 Work Item 详情失败");
            setDetail(null);
            setLoadError(errorMessage);
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [id, message]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const wi = detail?.work_item;

    const eventColumns = useMemo(() => [
        { title: "状态", dataIndex: "status", key: "status", width: 120, render: (value) => <Tag>{value}</Tag> },
        { title: "Step", dataIndex: "step", key: "step", width: 160 },
        { title: "错误", dataIndex: "error_message", key: "error_message", render: (value) => value || <Text type="secondary">—</Text> },
        { title: "创建时间", dataIndex: "created_at", key: "created_at", width: 190, render: (value) => value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "—" },
        { title: "完成时间", dataIndex: "completed_at", key: "completed_at", width: 190, render: (value) => value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "—" },
    ], []);

    if (loading && !detail) {
        return <Skeleton active paragraph={{ rows: 10 }} />;
    }

    if (loadError) {
        return <Alert type="error" showIcon message="Work Item 详情不可用" description={loadError} />;
    }

    if (!wi) {
        return <Empty description="未找到 Work Item" />;
    }

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <Space>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/mail-hub/ops/work-items")}>
                        返回列表
                    </Button>
                    <Title level={4} style={{ margin: 0 }}>
                        <NodeIndexOutlined style={{ marginRight: 8 }} />
                        {wi.item_type}
                    </Title>
                    <Tag color={statusColor(wi.status)}>{wi.status}</Tag>
                </Space>
                <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
                    刷新
                </Button>
            </div>

            <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <Card size="small" title="执行概览">
                    <Descriptions bordered size="small" column={3}>
                        <Descriptions.Item label="Work Item ID" span={2}>{wi.id}</Descriptions.Item>
                        <Descriptions.Item label="Status"><Tag color={statusColor(wi.status)}>{wi.status}</Tag></Descriptions.Item>
                        <Descriptions.Item label="Retry">{wi.retry_count} / {wi.max_retries}</Descriptions.Item>
                        <Descriptions.Item label="Execution">{wi.execution_kind || "—"}{wi.pool_profile ? ` / ${wi.pool_profile}` : ""}</Descriptions.Item>
                        <Descriptions.Item label="Priority">{wi.priority}</Descriptions.Item>
                        <Descriptions.Item label="Locked By">{formatValue(wi.locked_by)}</Descriptions.Item>
                        <Descriptions.Item label="Locked At">{formatValue(wi.locked_at)}</Descriptions.Item>
                        <Descriptions.Item label="Completed At">{formatValue(wi.completed_at)}</Descriptions.Item>
                        <Descriptions.Item label="Result Summary" span={3}>{formatValue(wi.result_summary)}</Descriptions.Item>
                    </Descriptions>
                </Card>

                <Card size="small" title="Work Item 全字段">
                    <Descriptions bordered size="small" column={2}>
                        {WORK_ITEM_FIELD_ORDER.map((field) => (
                            <Descriptions.Item key={field} label={field} span={["description", "result_summary"].includes(field) ? 2 : 1}>
                                {formatValue(wi[field])}
                            </Descriptions.Item>
                        ))}
                    </Descriptions>
                </Card>

                <Card size="small" title="metadata / result_data JSON">
                    <Space direction="vertical" size={12} style={{ width: "100%" }}>
                        <div>
                            <Text strong>metadata</Text>
                            <JsonBlock value={wi.metadata} />
                        </div>
                        <div>
                            <Text strong>result_data</Text>
                            <JsonBlock value={wi.result_data} />
                        </div>
                    </Space>
                </Card>

                <Card size="small" title="Dead Letter">
                    {detail.dead_letters.length ? (
                        <List
                            size="small"
                            dataSource={detail.dead_letters}
                            renderItem={(item) => (
                                <List.Item>
                                    <Descriptions size="small" column={2} style={{ width: "100%" }}>
                                        <Descriptions.Item label="ID">{item.id}</Descriptions.Item>
                                        <Descriptions.Item label="Moved At">{formatValue(item.moved_to_dead_at)}</Descriptions.Item>
                                        <Descriptions.Item label="Retry Count">{formatValue(item.retry_count)}</Descriptions.Item>
                                        <Descriptions.Item label="Acknowledged">{formatValue(item.acknowledged_at)}</Descriptions.Item>
                                        <Descriptions.Item label="Error" span={2}>{formatValue(item.error_message)}</Descriptions.Item>
                                        <Descriptions.Item label="Resolution" span={2}>{formatValue(item.resolution)}</Descriptions.Item>
                                    </Descriptions>
                                </List.Item>
                            )}
                        />
                    ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="无 Dead Letter 记录" />}
                </Card>

                <Card size="small" title="Processing Events">
                    <Table
                        rowKey="id"
                        size="small"
                        columns={eventColumns}
                        dataSource={detail.processing_events}
                        pagination={false}
                        locale={{ emptyText: "后端明确返回无处理事件" }}
                        scroll={{ x: 900 }}
                    />
                </Card>

                <Card size="small" title="关联邮件摘要">
                    <Space direction="vertical" size={12} style={{ width: "100%" }}>
                        <Descriptions bordered size="small" column={2} title="Mailbox Receipt">
                            <Descriptions.Item label="ID">
                                <Text copyable>{detail.receipt_summary.id}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Mailbox ID">{detail.receipt_summary.mailbox_id}</Descriptions.Item>
                            <Descriptions.Item label="Remote Folder">{formatValue(detail.receipt_summary.remote_folder)}</Descriptions.Item>
                            <Descriptions.Item label="Remote UID">{formatValue(detail.receipt_summary.remote_uid)}</Descriptions.Item>
                            <Descriptions.Item label="Received At">{formatValue(detail.receipt_summary.received_at)}</Descriptions.Item>
                            <Descriptions.Item label="Collab Status">{detail.receipt_summary.collab_status}</Descriptions.Item>
                        </Descriptions>
                        <Descriptions bordered size="small" column={2} title="Canonical Message">
                            <Descriptions.Item label="ID">{detail.canonical_message_summary.id}</Descriptions.Item>
                            <Descriptions.Item label="Message ID">{detail.canonical_message_summary.message_id}</Descriptions.Item>
                            <Descriptions.Item label="Subject" span={2}>{formatValue(detail.canonical_message_summary.subject)}</Descriptions.Item>
                            <Descriptions.Item label="From">{detail.canonical_message_summary.from_display ? `${detail.canonical_message_summary.from_display} <${detail.canonical_message_summary.from_address}>` : detail.canonical_message_summary.from_address}</Descriptions.Item>
                            <Descriptions.Item label="Date">{formatValue(detail.canonical_message_summary.date)}</Descriptions.Item>
                            <Descriptions.Item label="Receipt Flags" span={2}>
                                <JsonBlock value={detail.receipt_summary.processing_flags} />
                            </Descriptions.Item>
                            <Descriptions.Item label="Canonical Flags" span={2}>
                                <JsonBlock value={detail.canonical_message_summary.processing_flags} />
                            </Descriptions.Item>
                        </Descriptions>
                            {detail.classifications.length ? (
                                <Card size="small" title="Classification">
                                    <List
                                        size="small"
                                        dataSource={detail.classifications}
                                        renderItem={(item) => (
                                            <List.Item>
                                                <Space wrap>
                                                    <Tag color={item.is_active ? "blue" : "default"}>{item.classification_kind}</Tag>
                                                    <Tag>{item.category}{item.sub_category ? ` / ${item.sub_category}` : ""}</Tag>
                                                    {item.confidence !== null && item.confidence !== undefined ? <Text type="secondary">confidence {item.confidence}</Text> : null}
                                                    <Text type="secondary">{dayjs(item.created_at).format("YYYY-MM-DD HH:mm:ss")}</Text>
                                                </Space>
                                            </List.Item>
                                        )}
                                    />
                                </Card>
                            ) : null}
                    </Space>
                </Card>

                <Card size="small" title="APFM execution/session 线索">
                    <Space direction="vertical" size={12} style={{ width: "100%" }}>
                        <Alert type="info" showIcon message={detail.apfm_clues.limitation} />
                        <Descriptions bordered size="small" column={2}>
                            {Object.entries(detail.apfm_clues.direct_fields).map(([key, value]) => (
                                <Descriptions.Item key={key} label={key}>{formatValue(value)}</Descriptions.Item>
                            ))}
                        </Descriptions>
                        {detail.apfm_clues.extracted_ids.length ? (
                            <List
                                size="small"
                                dataSource={detail.apfm_clues.extracted_ids}
                                renderItem={(item) => (
                                    <List.Item>
                                        <Space>
                                            <Tag>{item.source}</Tag>
                                            <Text>{item.key}</Text>
                                            <Text copyable>{item.value}</Text>
                                        </Space>
                                    </List.Item>
                                )}
                            />
                        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="metadata/result_data 中未发现 execution/session ID" />}
                    </Space>
                </Card>
            </Space>
        </div>
    );
};

export default WorkItemDetailPage;
